import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import Seo from "@/components/Seo";

const VS = {
  "mexico-relocation-guide": {
    name: "Mexico Relocation Guide",
    rows: [
      ["Pricing transparency", "Interactive cost calculator with live ranges before you talk to anyone", "Course and consultation pricing, quoted after you engage"],
      ["Delivery mode", "Digital-first planning plus hands-on, door-to-door driving and transport", "Primarily online courses and consulting"],
      ["Pet relocation", "Door-to-door pet transport with our own background-checked drivers", "Guidance and referrals"],
      ["Who it fits", "US residents who want one team from first plan to arrival day", "DIY movers who want to learn and self-manage"],
    ],
    faqs: [
      ["What's the main difference between Hola Movers and Mexico Relocation Guide?",
       "Mexico Relocation Guide teaches you how to run your own move. Hola Movers plans it with you and can physically do it — drive you, your vehicle and your pets door-to-door to Mérida."],
      ["Which is cheaper?",
       "It depends on how much you do yourself. Our cost calculator shows transparent package ranges before you spend anything; an intro call is $75."],
      ["Can I use both?",
       "Absolutely. Many people research broadly and then bring us in for the parts they don't want to DIY — like the drive or pet transport."],
    ],
  },
  "focus-on-mexico": {
    name: "Focus on Mexico",
    rows: [
      ["Pricing transparency", "Interactive cost calculator with live ranges before you talk to anyone", "Seminar-based programs, pricing shared through their process"],
      ["Delivery mode", "Digital-first, one-on-one, at your own pace", "Group seminars and programs, longest-running in the category"],
      ["Pet relocation", "Door-to-door pet transport with our own background-checked drivers", "Guidance within broader programs"],
      ["Who it fits", "People who want a personal plan and hands-on help", "People who like learning in a group seminar format"],
    ],
    faqs: [
      ["What's the main difference between Hola Movers and Focus on Mexico?",
       "Focus on Mexico is a long-established seminar-based program. Hola Movers is digital-first and personal: a calculator, a timeline, an intro call, and drivers who take you door-to-door."],
      ["Is Hola Movers good for people who don't speak Spanish?",
       "Yes — that's exactly who we built it for. Everything is in English, and our drivers are bilingual."],
      ["How do I compare costs?",
       "Open our cost calculator and get a live range in two minutes — no consultation required to see prices."],
    ],
  },
};

export default function VsPage() {
  const { slug } = useParams();
  const data = VS[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!data) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-32 text-center">
        <h1 className="text-3xl font-light text-charcoal-900">Comparison not found</h1>
        <Link to="/" className="mt-6 inline-block text-terracotta underline underline-offset-4">Back home →</Link>
      </div>
    );
  }
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map(([q, a]) => ({
      "@type": "Question", name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
  return (
    <div data-testid={`page-vs-${slug}`}>
      <Seo title={`Hola Movers vs ${data.name}: Which Mexico Relocation Service Fits You?`}
        path={`/vs/${slug}`} jsonLd={faqSchema}
        description={`An honest comparison of two Mexico relocation services — Hola Movers and ${data.name} — on pricing transparency, delivery mode, pet relocation and fit.`} />
      <section className="bg-sand-100">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
          <div className="max-w-2xl animate-fade-up">
            <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-4">Mexico relocation services, compared</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900 leading-[1.05]">
              Hola Movers vs {data.name}
            </h1>
            <p className="mt-6 text-lg text-charcoal-600 leading-relaxed">
              Both help US residents move to Mexico. Here's an honest look at how we differ, so you can
              pick what actually fits your move.
            </p>
          </div>
        </div>
      </section>
      <div className="mx-auto max-w-5xl px-6 lg:px-12 py-16 space-y-16">
        <div className="overflow-x-auto rounded-3xl border border-sand-200 bg-white" data-testid="vs-table">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-sand-200 text-charcoal-500">
                <th className="p-5 font-medium"></th>
                <th className="p-5 font-medium text-charcoal-800">Hola Movers</th>
                <th className="p-5 font-medium">{data.name}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(([label, us, them]) => (
                <tr key={label} className="border-b border-sand-100 last:border-0 align-top">
                  <td className="p-5 font-medium text-charcoal-800 whitespace-nowrap">{label}</td>
                  <td className="p-5 text-charcoal-700"><Check size={15} className="inline mr-1.5 text-sage" />{us}</td>
                  <td className="p-5 text-charcoal-500"><Minus size={15} className="inline mr-1.5 text-charcoal-300" />{them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div data-testid="vs-faq">
          <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-6">Common questions</h2>
          <div className="space-y-6">
            {data.faqs.map(([q, a]) => (
              <div key={q} className="rounded-2xl bg-white border border-sand-200 p-6">
                <h3 className="font-medium text-charcoal-800">{q}</h3>
                <p className="mt-2 text-charcoal-600 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl bg-sage-light p-10 text-center">
          <h2 className="text-2xl font-normal text-charcoal-800">See your numbers first — no consultation required</h2>
          <Link to="/cost-calculator" className="mt-6 inline-block rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
            Open the cost calculator
          </Link>
        </div>
      </div>
    </div>
  );
}
